// Client-side validation utilities for forms

export const validateEmail = (email) => {
  if (!email) {
    return 'Email address is required.';
  }
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return 'Please enter a valid email address.';
  }
  
  return null;
};

export const validatePassword = (password) => {
  if (!password) {
    return 'Password is required.';
  }
  
  if (password.length < 8) {
    return 'Password must be at least 8 characters long.';
  }
  
  if (!/[A-Z]/.test(password)) {
    return 'Password must contain at least one uppercase letter.';
  }
  
  if (!/[a-z]/.test(password)) {
    return 'Password must contain at least one lowercase letter.';
  }
  
  if (!/\d/.test(password)) {
    return 'Password must contain at least one number.';
  }
  
  const specialCharRegex = /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/;
  if (!specialCharRegex.test(password)) {
    return 'Password must contain at least one special character (!@#$%^&*()_+-=[]{}|;:,.<>?).';
  }
  
  return null;
};

export const validateName = (name, fieldName = 'Name') => {
  if (!name || !name.trim()) {
    return `${fieldName} is required.`;
  }
  
  if (name.trim().length < 2) {
    return `${fieldName} must be at least 2 characters long.`;
  }
  
  return null;
};

export const validatePasswordConfirm = (password, passwordConfirm) => {
  if (!passwordConfirm) {
    return 'Please confirm your password.';
  }
  
  if (password !== passwordConfirm) {
    return "Passwords don't match. Please ensure both password fields are identical.";
  }
  
  return null;
};

// Real-time validation for signup form
export const validateSignupForm = (formData) => {
  const errors = {};
  
  const emailError = validateEmail(formData.email);
  if (emailError) errors.email = emailError;
  
  const passwordError = validatePassword(formData.password);
  if (passwordError) errors.password = passwordError;
  
  const firstNameError = validateName(formData.first_name, 'First name');
  if (firstNameError) errors.first_name = firstNameError;
  
  const lastNameError = validateName(formData.last_name, 'Last name');
  if (lastNameError) errors.last_name = lastNameError;
  
  const passwordConfirmError = validatePasswordConfirm(formData.password, formData.password_confirm);
  if (passwordConfirmError) errors.password_confirm = passwordConfirmError;

  if (!formData.user_type) {
    errors.user_type = 'Please tell us who you are.';
  }

  if (formData.user_type === 'other' && !formData.user_type_other?.trim()) {
    errors.user_type_other = 'Please describe your role.';
  }

  return errors;
};

export const getPasswordStrength = (password) => {
  if (!password) return { score: 0, text: 'Enter password', color: 'gray' };
  
  let score = 0;
  const checks = {
    length: password.length >= 8,
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    numbers: /\d/.test(password),
    special: /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(password)
  };
  
  score = Object.values(checks).filter(Boolean).length;
  
  if (score <= 2) {
    return { score, text: 'Weak', color: 'red' };
  } else if (score <= 4) {
    return { score, text: 'Good', color: 'yellow' };
  } else {
    return { score, text: 'Strong', color: 'green' };
  }
};